// Super-admin blog management: per-business posts + image library.
// Reads/writes go through RLS policies that require is_super_admin().

import { supabase } from '../lib/supabase';

export interface BlogPost {
  id: string;
  business_id: string;
  title: string;
  slug: string;
  body_html: string;
  body_markdown: string;
  excerpt: string | null;
  meta_description: string | null;
  featured_image_url: string | null;
  status: 'draft' | 'published';
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlogImage {
  id: string;
  business_id: string;
  storage_path: string;
  public_url: string;
  filename: string | null;
  alt_text: string | null;
  used_in_post_id: string | null;
  created_at: string;
}

export interface SaveBlogPostInput {
  id?: string;
  business_id: string;
  title: string;
  slug?: string;
  body_markdown: string;
  excerpt?: string | null;
  meta_description?: string | null;
  featured_image_url?: string | null;
  status: 'draft' | 'published';
  /** ISO timestamp. Future = scheduled (public API hides it until then). */
  published_at?: string | null;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/**
 * Minimal markdown → HTML for the manual editor (headings, bold/italic,
 * links, images, lists, paragraphs). The AI phase will produce HTML directly.
 */
export function markdownToHtml(md: string): string {
  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const inline = (s: string): string =>
    s
      .replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, '<img src="$2" alt="$1" loading="lazy" />')
      .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2">$1</a>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>');

  const blocks = md.replace(/\r\n/g, '\n').split(/\n{2,}/);
  const html: string[] = [];

  for (const rawBlock of blocks) {
    const block = rawBlock.trim();
    if (!block) continue;

    const heading = block.match(/^(#{1,4})\s+(.*)$/s);
    if (heading) {
      const level = heading[1].length + 1; // # → h2 (h1 is the post title)
      html.push(`<h${level}>${inline(escapeHtml(heading[2].trim()))}</h${level}>`);
      continue;
    }

    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length > 0 && lines.every((l) => /^[-*]\s+/.test(l))) {
      const items = lines.map((l) => `<li>${inline(escapeHtml(l.replace(/^[-*]\s+/, '')))}</li>`);
      html.push(`<ul>${items.join('')}</ul>`);
      continue;
    }
    if (lines.length > 0 && lines.every((l) => /^\d+\.\s+/.test(l))) {
      const items = lines.map((l) => `<li>${inline(escapeHtml(l.replace(/^\d+\.\s+/, '')))}</li>`);
      html.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    html.push(`<p>${inline(escapeHtml(block)).replace(/\n/g, '<br />')}</p>`);
  }

  return html.join('\n');
}

export class BlogService {
  static async listPosts(businessId: string): Promise<BlogPost[]> {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });
    if (error) {
      console.error('listPosts:', error);
      return [];
    }
    return (data || []) as BlogPost[];
  }

  static async savePost(input: SaveBlogPostInput): Promise<{ success: boolean; message: string; post?: BlogPost }> {
    if (!supabase) return { success: false, message: 'Database connection not available' };

    const title = input.title.trim();
    if (!title) return { success: false, message: 'Title is required' };
    const slug = slugify(input.slug?.trim() || title);
    if (!slug) return { success: false, message: 'Slug could not be generated from the title' };

    const row = {
      business_id: input.business_id,
      title,
      slug,
      body_markdown: input.body_markdown,
      body_html: markdownToHtml(input.body_markdown),
      excerpt: input.excerpt?.trim() || null,
      meta_description: input.meta_description?.trim() || null,
      featured_image_url: input.featured_image_url || null,
      status: input.status,
      // Publishing without an explicit time = now.
      published_at:
        input.status === 'published' ? input.published_at || new Date().toISOString() : input.published_at || null,
      updated_at: new Date().toISOString(),
    };

    const query = input.id
      ? supabase.from('blog_posts').update(row).eq('id', input.id).select('*').single()
      : supabase.from('blog_posts').insert(row).select('*').single();

    const { data, error } = await query;
    if (error) {
      const message = error.code === '23505' ? `Slug "${slug}" is already used by another post for this business` : error.message;
      return { success: false, message };
    }
    return { success: true, message: 'Post saved', post: data as BlogPost };
  }

  static async deletePost(postId: string): Promise<{ success: boolean; message: string }> {
    if (!supabase) return { success: false, message: 'Database connection not available' };
    const { error } = await supabase.from('blog_posts').delete().eq('id', postId);
    return error ? { success: false, message: error.message } : { success: true, message: 'Post deleted' };
  }

  static async listImages(businessId: string): Promise<BlogImage[]> {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('blog_images')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });
    if (error) {
      console.error('listImages:', error);
      return [];
    }
    return (data || []) as BlogImage[];
  }

  static async uploadImage(
    businessId: string,
    file: File,
  ): Promise<{ success: boolean; message: string; image?: BlogImage }> {
    if (!supabase) return { success: false, message: 'Database connection not available' };

    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `${businessId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;

    const { error: uploadError } = await supabase.storage.from('blog-images').upload(path, file, {
      cacheControl: '31536000',
      upsert: false,
      contentType: file.type || 'image/jpeg',
    });
    if (uploadError) return { success: false, message: uploadError.message };

    const { data: urlData } = supabase.storage.from('blog-images').getPublicUrl(path);
    const { data, error } = await supabase
      .from('blog_images')
      .insert({
        business_id: businessId,
        storage_path: path,
        public_url: urlData.publicUrl,
        filename: file.name,
      })
      .select('*')
      .single();
    if (error) return { success: false, message: error.message };
    return { success: true, message: 'Image uploaded', image: data as BlogImage };
  }

  static async deleteImage(image: BlogImage): Promise<{ success: boolean; message: string }> {
    if (!supabase) return { success: false, message: 'Database connection not available' };
    await supabase.storage.from('blog-images').remove([image.storage_path]);
    const { error } = await supabase.from('blog_images').delete().eq('id', image.id);
    return error ? { success: false, message: error.message } : { success: true, message: 'Image deleted' };
  }
}
